import { createContext, useEffect, useState } from "react";

import {
  Box,
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Button,
  CloseButton,
  Dialog,
  handleClose,
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  pn,
  ScreenType,
  SearchBar,
  Stack,
  Typography,
  useParams,
} from "@compass/core";

import { ListFooter } from "components";
import {
  Permission,
  PermissionAction,
  StudentServiceIndexListDto,
  StudentServiceIndexListFilter,
  StudentServiceIndexListResource,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { StudentServiceIndexFilter } from "../StudentServiceIndexFilter";
import { StudentServiceIndexTable } from "../StudentServiceIndexListPage/StudentServiceIndexTable";
import { studentServiceIndexPageRoute } from "../StudentServiceIndexPage/StudentServiceIndexPage";
import { Border } from "devextreme-react/chart";

const routeName = "studentServiceIndexes";

class Params {
  screenType: ScreenType = "standard";
}

class OptionalParams {
  selectMode?: boolean = false;
}

export const studentServiceIndexListPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Service Index",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export type StudentServiceIndexListContextValue = {
  onSelectIndex?: (id: number) => void;
};

export const StudentServiceIndexListContext = createContext<StudentServiceIndexListContextValue>(
  {} as StudentServiceIndexListContextValue
);

export const StudentServiceIndexListPage = () => {
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);

  const ROOT_PERMISSION: Permission = Permission.StudentService;
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<StudentServiceIndexListFilter>({
    sortObjects: [{ property: pn<StudentServiceIndexListDto>("name"), isDesc: false }],
  });
  const [initListFilter] = useState<StudentServiceIndexListFilter>(listFilter);
  const [listItems, setListItems] = useState<StudentServiceIndexListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<StudentServiceIndexListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const [selectMode] = useState<boolean>(optionalParams.selectMode || false);

  const {
    tableContainerRef,
    loadList,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.studentServiceIndexes.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.studentServiceIndexes.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);
    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.studentServiceIndexes.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<StudentServiceIndexListFilter>({
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <StudentServiceIndexFilter
        filter={listFilter.studentServiceIndexFilter || {}}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.studentServiceIndexFilter = filter;
            })
          )
        }
      />
    ),
  });

  const pageHeader = (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="inherit">{studentServiceIndexListPageRoute.handle} </Typography>
      {params.screenType === "dialog" && <CloseButton theme="Dark" />}

      {!selectMode && isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
        <Button
          onClick={() =>
            navigate({
              pathname: studentServiceIndexPageRoute.getPath({
                id: 0,
                crudMode: "edit",
                screenType: "dialog",
              }),
            })
          }
          variant="outlined"
        >
          + New
        </Button>
      )}
    </Stack>

  );

  const pageBody = (
    <ListContainer
      customStyle={{ border: "1px solid #ddd" }}
      fullHeight
      header={
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
          {listFilterNode}
        </Stack>
      }
      tableContainer={
        <StudentServiceIndexTable
          rootPermission={ROOT_PERMISSION}
          tableContainerRef={tableContainerRef}
          studentServiceIndexes={listItems}
          resource={resource}
          listTotal={total}
          sortObjects={listFilter.sortObjects}
          sort={applySort}
          searchFocused={searchFocused}
          selectId={selectId}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          loadList={() => loadList(listFilter)}
          selectMode={selectMode}
        />
      }
      footer={
        <ListFooter
          total={total}
          size={listFilter.pageSize}
          page={listFilter.page}
          onPageChange={applyPage}
          onSizeChange={applyPageSize}
          onSortMethodChange={applySortMethod}
        />
      }
    />
  );

  return (
    <>
      {params.screenType === "standard" ? (
        <Page header={pageHeader}>{pageBody}</Page>
      ) : (
          <Dialog
            isForm
            open={true}
            onClose={() => handleClose(navigate)}
            width={params.screenType === "dialog" ? "90vw" : "70vw"}
            maxWidth={params.screenType === "dialog" ? "xl" : "lg"}
            header={pageHeader}
            content={pageBody}
          />
      )}

      <ListContext.Provider
        value={{
          loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
          clearSelectedIds: () => setSelectedIds([]),
        }}
      >
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
